import { Checkbox, FormControl, FormErrorMessage, Grid, HStack, Text } from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { Control, useForm } from "react-hook-form";

import { $ok } from "foundation-ts/commons";
import { Nullable } from "foundation-ts/types";

import { G1Input } from "@components/G1Input";
import { AsyncActionModal } from "@components/Modals/AsyncActionModal";
import { AsyncValidateContactInfosModalState } from "@components/Modals/ModalConstants";
import { Phonenumber } from "@components/Phonenumber/Phonenumber";
import { config } from "@/config/config";
import { G1Icon } from "@/utils/icons";
import { localtrans } from "@utils/LocalTranslations";

export const AsyncValidateContactInfosModal = (props: AsyncValidateContactInfosModalState) => {
    const { contactInfos, isOpen, senderName = '', size = 'md', title, onClose, onAccept } = props ;
    const [editLastname, setEditLastname] = useState(false);
    const [updatedLastname, setUpdatedLastname] = useState<Nullable<string>>(null);
    const [lastnameError, setLastnameError] = useState<string|undefined>(undefined);
    const [editFirstname, setEditFirstname] = useState(false);
    const [updatedFirstname, setUpdatedFirstname] = useState<Nullable<string>>(null);
    const [firstnameError, setFirstnameError] = useState<string|undefined>(undefined);
    const [editPhonenumber, setEditPhonenumber] = useState(false);
    const [updatedPhonenumber, setUpdatedPhonenumber] = useState<Nullable<string>>(null);
    const [badRecipientChecked, setBadRecipientChecked] = useState(contactInfos?.status === 'badRecipient');
    const [notusingPhonenumber, setNotusingPhonenumber] = useState(false);
    const lastnameFormRef = useRef<HTMLInputElement>(null);
    const firstnameFormRef = useRef<HTMLInputElement>(null);
    const {
        control,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm<{ phone: string }>({
        defaultValues: { phone: contactInfos?.phone ?? "" },
    });

    const regexp = new RegExp(/^\s*[a-zàâäéèêëïîôöùûüç]+([ '-][a-zàâäéèêëïîôöùûüç]+)*\s*$/i);
    const message = contactInfos?.status === 'badRecipient' ? 
        `Lors d'une précédente session de signature, vous avez refusé de signer en indiquant que vous n'êtiez pas le bon destinataire.\n${senderName} vous demande à nouveau de signer des documents.\n\nSi vous signez les documents en ayant validé (ou mis à jour) vos données, alors vous ne serez plus considéré, par la suite, comme 'Mauvais destinataire'.`
        : `Veuillez confirmer ou mettre à jour les informations fournies par ${senderName} :`;

    const handleValidatePhone = handleSubmit(async ({ phone }) => {
        setUpdatedPhonenumber(phone);
        setEditPhonenumber(false);
    });

    const handleAccept = async () => {
        await onAccept({
            email: contactInfos!.email,
            lastName: updatedLastname || contactInfos!.lastName,
            firstName: updatedFirstname || contactInfos!.firstName,
            phone: updatedPhonenumber || contactInfos!.phone || undefined
        }, badRecipientChecked, notusingPhonenumber);
    };

    const handleClose = async () => {
        await onClose() ;
    };

    const renderInputField = () => {
        return (
            <Grid templateColumns="200px 1fr 32px" rowGap={2} columnGap={2} alignItems="center" pl={5} pr={5}>
                <EditableField
                    label="Nom :"
                    value={updatedLastname?.toUpperCase() || contactInfos?.lastName}
                    isEditing={editLastname}
                    editAriaLabel="Mettre à jour votre nom"
                    error={lastnameError}
                    onEdit={() => setEditLastname(true)}
                    onValidate={() => {
                        const value = lastnameFormRef.current?.value;
                        if ($ok(value) && regexp.test(value!)) {
                            setEditLastname(false);
                            setUpdatedLastname(value?.trim());
                            setLastnameError(undefined);
                        } else {
                            setLastnameError("Le nom saisi contient des caractères invalides");
                        }
                    }}
                    onCancel={() => {
                        setEditLastname(false);
                        setLastnameError(undefined);
                    }}
                    renderInput={() => <G1Input component="form" flex="1" ref={lastnameFormRef} defaultValue={updatedLastname?.toUpperCase() || contactInfos?.lastName} textTransform="uppercase" />}
                />
                <EditableField
                    label="Prénom :"
                    value={updatedFirstname?.capitalize() || contactInfos?.firstName}
                    isEditing={editFirstname}
                    editAriaLabel="Mettre à jour votre prénom"
                    error={firstnameError}
                    onEdit={() => setEditFirstname(true)}
                    onValidate={() => {
                        const value = firstnameFormRef.current?.value;
                        if ($ok(value) && regexp.test(value!)) {
                            setEditFirstname(false);
                            setUpdatedFirstname(value?.trim());
                            setFirstnameError(undefined);
                        } else {
                            setFirstnameError("Le prénom saisi contient des caractères invalides");
                        }
                    }}
                    onCancel={() => {
                        setEditFirstname(false);
                        setFirstnameError(undefined);
                    }}
                    renderInput={() => <G1Input component="form" flex="1" ref={firstnameFormRef} defaultValue={updatedFirstname?.capitalize() || contactInfos?.firstName} />}
                />
                {$ok(contactInfos?.phone) && 
                    <>
                        <EditableField
                            label="Téléphone portable :"
                            value={updatedPhonenumber ?? contactInfos?.phone}
                            isEditing={editPhonenumber}
                            error={errors.phone?.message}
                            onEdit={() => {
                                reset({ phone: updatedPhonenumber ?? contactInfos?.phone ?? "" })
                                setEditPhonenumber(true)
                            }}
                            onValidate={handleValidatePhone}
                            onCancel={() => setEditPhonenumber(false)}
                            editAriaLabel="Mettre à jour mon numéro de téléphone portable"
                            renderInput={() => <Phonenumber height={40} authorizedCountries={config.app.gsmAuthorizedCountries} control={control as unknown as Control} />}
                        />
                        <Checkbox gridColumn="1 / -1" onChange={(e) => setNotusingPhonenumber(e.target.checked)}>Je n'autorise pas l'utilisation de mon numéro de téléphone</Checkbox>
                    </>
                }
                <Checkbox gridColumn="1 / -1" isChecked={badRecipientChecked} onChange={(e) => setBadRecipientChecked(e.target.checked)}>
                    {`Je ne suis ${contactInfos?.status === "badRecipient" ? "toujours" : ""} pas le bon destinataire`}
                </Checkbox>
                {badRecipientChecked && <Text gridColumn="1 / -1" color="red" fontSize="xs" fontStyle="italic">Après avoir validé, nous vous donnerons la possibilité de refuser de suite de signer le(s) document(s)</Text>}
            </Grid>
        );
    };

    useEffect(() => {
        setBadRecipientChecked(contactInfos?.status === "badRecipient");
    }, [contactInfos]);

    return <AsyncActionModal 
        title={title} 
        message={message}
        size={size} isOpen={isOpen}
        element={renderInputField()}
        confirm={localtrans('validate')} 
        cancel={localtrans('cancel')} 
        onClose={handleClose}
        onAccept={handleAccept}
    />;
};

type EditableFieldProps = {
    label: string;
    isEditing: boolean;
    editAriaLabel: string;
    value?: Nullable<string>;
    fallback?: string;
    error?: string;
    onEdit: () => void;
    onValidate: () => void;
    onCancel: () => void;
    renderInput: () => React.ReactNode;
};

const EditableField = (props: EditableFieldProps) => {
    const { label, value, fallback = "Non défini", isEditing, editAriaLabel, error, onEdit, onValidate, onCancel, renderInput } = props;
    return (
        <>
            <Text fontSize="md" fontWeight="medium" color="gray.600">{label}</Text>
            {isEditing ? (
                <FormControl isInvalid={!!error} gridColumn="2 / -1">
                    <HStack>
                        { renderInput() }
                        <G1Icon.Check
                            aria-label={`Valider le changement de ${label.toLowerCase()}`}
                            color="green"
                            size="20px"
                            cursor="pointer"
                            onClick={onValidate}
                        />
                        <G1Icon.Cancel
                            aria-label={`Annuler le changement de ${label.toLowerCase()}`}
                            color="red"
                            size="22px"
                            cursor="pointer"
                            onClick={onCancel}
                        />
                    </HStack>
                    <FormErrorMessage minH="20px" mt={1}>{error}</FormErrorMessage>
                </FormControl>
            ) : (
                <>
                    <Text fontSize="md">{value || fallback}</Text>
                    <G1Icon.Edit onClick={onEdit} cursor="pointer" aria-label={editAriaLabel} />
                </>
            )}
        </>
    );
};